import { Injectable } from '@angular/core';
import { Router     } from '@angular/router';

import { FormatDatesService, HelperService, StatusService, UserService } from '@shared/services';
import { ActivityReportBasicSimplified, ActivityReportSimplified, AssignmentBasicTitle, EmployeeBasic, VacationRequestBasic } from '@shared/factories';
import { ExternalEmployeeCellExtras, ExternalWorkLocationModel, OverviewActivityReports, OverviewVacationRequests, StatusCellExtras, StatusModel, TableBodyCellEntryModel, TableBodyCellNestedEntryModel, TableListIconModel } from '@shared/models';

@Injectable()
export class OverviewTableService {
  constructor (
    private router:        Router,
    private userService:   UserService,
    private helperService: HelperService,
    private statusService: StatusService,
  ) { }

  getExternalEmployeeCell(ee: EmployeeBasic, extra: ExternalEmployeeCellExtras = {}): TableBodyCellEntryModel {
    let children: TableBodyCellNestedEntryModel[] = [
      { class: 'am-flex-center am-flex-tablet-column circle-40 font-s color-blue bg-light-blue', value: ee?.first_name && ee?.last_name ? `${ee.first_name[0]}${ee.last_name[0]}` : 'n/a' },
      { class: 'am-flex-column', children: [
        { class: 'am-flex-column', children: [
          { class: 'font-500 lh-m', value: ee?.nameReverse || 'n/a' },
          { class: 'font-s roboto', value: ee?.personal_number      },
        ]},
        extra?.extraChildren ? { class: 'am-flex-column', children: extra.extraChildren } : null
      ].filter(i => i)}
    ];

    if (extra?.statusDot) children.push({ class: `xs-show-table mla-i status-icon ${ extra.statusDot }` });

    return ({
      xs_label: `Mitarbeiter ${ this.userService.isInternal ? '(Pers.Nr.)' : '' }`, children,
      class:    `name-cell am-flex-align gap-xs ${ this.userService.isInternal ? 'hover-active' : '' }`,
      click: () => this.userService.isInternal &&this.router.navigate(['/time-tracking/ee-details', ee?.id])
    });
  }

  getActivityReportDateCell(wp: OverviewActivityReports): TableBodyCellEntryModel {
    return {
      xs_label: 'Eingang', class: `date-cell tooltip-hover roboto ${this.statusService.initialDateState(wp) ? 'stop-hover' : '' }`, value: this.initialStateDateString(wp),
      pageOnly: ['dashboard', this.userService.isCustomer ? 'archive' : 'skip'],
      children: [{
        skip:     !this.statusService.initialDateState(wp),
        class:    `web-tooltip short word-no-wrap ${this.statusService.initialDateState(wp)?.color} ${wp instanceof ActivityReportSimplified ? 'bottom' : 'right'}`,
        children: [{ value: this.statusService.initialDateState(wp)?.label }]
      }],
      valueClass: `color-${this.statusService.initialDateState(wp)?.color}`,
    };
  }

  getAssignmentCells(a: AssignmentBasicTitle, ellipsis: boolean = null): TableBodyCellEntryModel[] {
    return [
      { xs_label: 'Einsatz', class: 'title-cell', skip: !a.mapped, children: [
        { class: 'am-flex-column', children: [
          { class: 'font-500',      value: a.mapped?.meta  },
          { class: 'font-s roboto', value: a.mapped?.dates }
        ]}
      ]},
      { xs_label: 'Einsatz', class: `title-cell ${ellipsis && 'ellipsis'}`, value: a.title, skip: !!a.mapped }
    ];
  }

  getWeekCell(calendarWeek: string, date: string): TableBodyCellEntryModel {
    return { xs_label: 'Zeitraum', class: 'week-cell', children: [
      { class: 'am-flex-column', children: [
        { class: 'font-500',      value: calendarWeek },
        { class: 'font-s roboto', value: date         }
      ]}
    ]};
  }

  getStatusCell(entry: OverviewActivityReports | OverviewVacationRequests, extra: StatusCellExtras = {}): TableBodyCellEntryModel {
    return {
      xs_label: null,
      class:   'status-cell xs-hide stop-hover',
      children: this.helperService.parceTableListIconsWeb(this.getStatusChildCell(entry, extra)), internalOnly: extra.internalOnly, pageOnly: extra.pageOnly
    };
  }

  getLocationCell(location: ExternalWorkLocationModel): TableBodyCellEntryModel {
    return { xs_label: 'Niederlassung', class: 'location-cell', children: [
      { class: 'am-flex-column', children: [
        { class: 'font-500',      value: location?.description || '-' },
        { class: 'font-s roboto', value: location?.identifier  || '-' }
      ]}
    ]};
  }

  getExternalEmployeeCellPlaceholder(): TableBodyCellEntryModel {
    let children: any[] = [
      { class: 'circle-40 loading-animate'},
      { class: 'am-flex-column am-flex-1 gap-xxs', children: [
        { class: 'width-max-150 width-full height-l  loading-animate' },
        { class: 'width-max-100 width-full height-14 loading-animate' }
      ]}
    ];
    return ({ xs_label: ' ', class: 'name-cell am-flex-align gap-xs', children });
  }

  getAssignmentCellsPlaceholder(): TableBodyCellEntryModel[] {
    return [{ 
      xs_label: ' ', class: 'title-cell am-flex', children: [
        { class: 'am-flex-column am-flex-1 gap-xxs', children: [
          { class: 'width-half               height-l  loading-animate' },
          { class: 'width-max-130 width-full height-14 loading-animate' }
        ]}
      ]}
    ];
  }

  getWeekCellPlaceholder(): TableBodyCellEntryModel {
    return { xs_label: ' ', class: 'week-cell', children: [
      { class: 'am-flex-column am-flex-1 gap-xxs', children: [
        { class: 'width-40      height-l  loading-animate'  },
        { class: 'width-max-130 height-14 loading-animate' }
      ]}
    ]};
  }

  getLocationCellPlaceholder(): TableBodyCellEntryModel {
    return { xs_label: ' ', class: 'location-cell', children: [
      { class: 'am-flex-column am-flex-1 gap-xxs', children: [
        { class: 'width-120 height-l  loading-animate' },
        { class: 'width-80  height-14 loading-animate' }
      ]}
    ]};
  }

  private initialStateDateString(wp: OverviewActivityReports): string {
    let format = wp instanceof ActivityReportSimplified ? 'dd.MM.yyyy' : 'dd.MM.yyyy HH:mm';
    return FormatDatesService.formatDate((wp as ActivityReportSimplified)?.customer_reviewed_at || wp?.created_at, format);
  }

  private getStatusChildCell(entry: OverviewActivityReports | OverviewVacationRequests, extra: StatusCellExtras = {}): TableListIconModel[] {
    return [{ show: true, icon: `status-icon ${this.getStatus(entry, extra)?.color}`, color: this.getStatus(entry, extra)?.color, tooltipTexts: [this.getStatus(entry, extra)?.label].filter(t => t) }];
  }

  getStatus(entry: OverviewActivityReports | OverviewVacationRequests, extra: StatusCellExtras = {}): StatusModel {
    if (entry instanceof ActivityReportBasicSimplified) return this.statusService.getStatusActivityReport( entry, extra);
    if (entry instanceof VacationRequestBasic         ) return this.statusService.getStatusVacationRequest(entry, extra);
  }

}